import { useEffect, useState } from "react";
import {
  Grid,
  Switch,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Box,
  Tooltip,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { createStyles, makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  UpsertWhatsappConfigAction,
  TestWhatsappConfigAction,
} from "../../../../../store/Ancillary/WhatsappConfig/Action";

const WhatsAppGeneralConnfig = () => {
  const useStyles = makeStyles((theme) =>
    createStyles({
      Paper: {
        boxShadow:
          theme.palette.mode === "dark"
            ? "rgb(0 0 0 / 20%) 0px 0px 2px 0px, rgb(0 0 0 / 12%) 0px 12px 24px -4px"
            : "0px 3px 3px -2px #cbc3c3, 0px 3px 4px 0px rgb(242 242 242), 0px 1px 8px 0px rgb(242 242 242)",
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: "#f3edfd",
        position: "relative",
      },
      Backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        position: "absolute",
        borderRadius: "inherit",
      },
    }),
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const { whatsappConfigData, isLoading, isTesting } = useSelector(
    (state) => state?.AncillaryReducer?.WhatsappConfigReducer || {},
  );
  const initialValues = {
    whatsapp_meta_token: "",
    phone_number_id: "",
    whatsapp_business_number: "",
    display_phone_number: "",
    verified_name: "",
    connection_status: "NOT_CONNECTED",
    token_configured: false,
    team_whatsapp_attendance_applicable: false,
    individual_whatsapp_attendance_applicable: false,
    whatsapp_configuration_pk: null,
  };

  const [config, setConfig] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [showToken, setShowToken] = useState(false);

  const handleInputChange = (value, name) => {
    setConfig((prev) => {
      const isCredentialField =
        name === "whatsapp_meta_token" || name === "phone_number_id";
      return {
        ...prev,
        [name]: value,
        ...(isCredentialField ? { connection_status: "NOT_CONNECTED" } : {}),
      };
    });
    if (Object.values(errors).some((res) => res !== "")) {
      validate({ ...config, [name]: value });
    }
  };
  const validate = (fieldValues) => {
    let temp = { ...errors };

    if ("whatsapp_meta_token" in fieldValues) {
      if (!config?.token_configured || fieldValues.whatsapp_meta_token?.trim()) {
        temp.whatsapp_meta_token = fieldValues.whatsapp_meta_token?.trim()
          ? ""
          : "Meta access token is required.";
      } else {
        temp.whatsapp_meta_token = "";
      }
    }

    if ("phone_number_id" in fieldValues) {
      temp.phone_number_id = fieldValues.phone_number_id?.trim()
        ? ""
        : "WhatsApp Phone Number ID is required.";
    }

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSaveTextConfig = () => {
    if (!validate(config)) return;

    dispatch(
      TestWhatsappConfigAction(
        {
          whatsapp_meta_token: config?.whatsapp_meta_token?.trim(),
          whatsapp_business_number: config?.phone_number_id?.trim(),
          show: true
        },
        (res) => {
          if (res) {
            const resData = res?.data?.data || {};
            const updatedDisplayNumber =
              resData?.display_phone_number || config?.display_phone_number;
            const updatedVerifiedName =
              resData?.verified_name || config?.verified_name;

            setConfig((prev) => ({
              ...prev,
              connection_status: "CONNECTED",
              display_phone_number: updatedDisplayNumber,
              whatsapp_business_number: updatedDisplayNumber || prev.whatsapp_business_number,
              verified_name: updatedVerifiedName,
            }));

            const payload = {
              ...config,
              whatsapp_meta_token: config?.whatsapp_meta_token?.trim(),
              phone_number_id: config?.phone_number_id?.trim(),
              whatsapp_business_number: updatedDisplayNumber || config?.whatsapp_business_number,
              display_phone_number: updatedDisplayNumber,
              verified_name: updatedVerifiedName,
              connection_status: "CONNECTED",
              whatsapp_configuration_fk: config?.whatsapp_configuration_pk || null,
            };
            dispatch(UpsertWhatsappConfigAction(payload));
          } else {
            setConfig((prev) => ({
              ...prev,
              connection_status: "ERROR",
            }));
          }
        },
      ),
    );
  };

  const handleToggleSwitch = (newState, control_type) => {
    setConfig((prev) => ({ ...prev, [control_type]: newState }));

    const isTeam =
      control_type === "team_whatsapp_attendance_applicable"
        ? newState
        : config?.team_whatsapp_attendance_applicable;
    const isIndividual =
      control_type === "individual_whatsapp_attendance_applicable"
        ? newState
        : config?.individual_whatsapp_attendance_applicable;

    const payload = {
      ...config,
      whatsapp_meta_token: config?.whatsapp_meta_token,
      phone_number_id: config?.phone_number_id,
      whatsapp_business_number: config?.whatsapp_business_number || config?.display_phone_number,
      team_whatsapp_attendance_applicable: isTeam,
      individual_whatsapp_attendance_applicable: isIndividual,
      whatsapp_configuration_fk: config?.whatsapp_configuration_pk || null,
    };

    dispatch(UpsertWhatsappConfigAction(payload));
  };

  useEffect(() => {
    if (whatsappConfigData && whatsappConfigData?.length > 0) {
      const data = whatsappConfigData[whatsappConfigData?.length - 1];
      const initialData = {
        whatsapp_meta_token: data?.whatsapp_meta_token || "",
        phone_number_id: data?.phone_number_id || "",
        whatsapp_business_number: data?.whatsapp_business_number || "",
        display_phone_number: data?.whatsapp_business_number || config?.display_phone_number || "",
        verified_name: config?.verified_name || "",
        connection_status: config?.connection_status || "NOT_CONNECTED",
        token_configured: !!data?.token_configured,
        team_whatsapp_attendance_applicable:
          data?.team_whatsapp_attendance_applicable ?? false,
        individual_whatsapp_attendance_applicable:
          data?.individual_whatsapp_attendance_applicable ?? false,
        whatsapp_configuration_pk: data?.whatsapp_configuration_pk || null,
      };

      setConfig(initialData);

      if (initialData.whatsapp_meta_token && initialData.phone_number_id) {
        dispatch(
          TestWhatsappConfigAction(
            {
              whatsapp_meta_token: initialData.whatsapp_meta_token.trim(),
              whatsapp_business_number: initialData.phone_number_id.trim(),
            },
            (res) => {
              if (res?.data?.statuscode === 200) {
                const resData = res?.data?.data || {};
                setConfig((prev) => ({
                  ...prev,
                  connection_status: resData?.connection_status || "CONNECTED",
                  display_phone_number:
                    resData?.display_phone_number || prev.display_phone_number,
                  whatsapp_business_number:
                    resData?.display_phone_number || prev.whatsapp_business_number,
                  verified_name: resData?.verified_name || prev.verified_name,
                }));
              } else {
                setConfig((prev) => ({
                  ...prev,
                  connection_status: "ERROR",
                }));
              }
            },
          ),
        );
      }
    }
  }, [whatsappConfigData, dispatch]);

  const renderStatusChip = () => {
    switch (config?.connection_status) {
      case "CONNECTED":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon style={{ color: "#2e7d32" }} />}
            label="CONNECTED"
            variant="outlined"
            sx={{
              borderColor: "#2e7d32",
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              fontWeight: 600,
            }}
          />
        );
      case "ERROR":
        return (
          <Chip
            icon={<ErrorOutlineIcon style={{ color: "#d32f2f" }} />}
            label="ERROR"
            variant="outlined"
            sx={{
              borderColor: "#d32f2f",
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              fontWeight: 600,
            }}
          />
        );
      default:
        return (
          <Chip
            icon={<WarningAmberIcon style={{ color: "#757575" }} />}
            label="NOT CONNECTED"
            variant="outlined"
            sx={{
              borderColor: "#757575",
              color: "#757575",
              backgroundColor: "#f5f5f5",
              fontWeight: 600,
            }}
          />
        );
    }
  };

  return (
    <Paper
      className={classes.Paper}
      sx={{
        borderRadius: "16px",
        width: "100%",
      }}
    >
      <Backdrop className={classes.Backdrop} open={!!isLoading || !!isTesting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ p: 1, py: 0 }}>
        {/* ---- Section 1: Meta Cloud API Configuration ---- */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              pr: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                WhatsApp Cloud API Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure Meta WhatsApp Cloud API credentials and connection status.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                className="mpl-primary-btn"
                disabled={!!isLoading || !!isTesting}
                onClick={handleSaveTextConfig}
              >
                {isTesting || isLoading ? "Saving..." : "Save Configuration"}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="flex-start">
            {/* Field 1: WhatsApp Meta Access Token */}
            <Grid item xs={12} sm={5.5} sx={{ mb: 1 }}>
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    color="inherit"
                    variant="body1"
                    sx={{ fontWeight: 500 }}
                  >
                    Meta Access Token
                  </Typography>
                  <Tooltip
                    title="Enter the access token for your Meta WhatsApp Cloud API."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  type={showToken ? "text" : "password"}
                  variant="outlined"
                  placeholder={
                    config?.token_configured
                      ? "Token is configured (Enter new token to replace)"
                      : "Enter Meta Access Token"
                  }
                  value={config?.whatsapp_meta_token || ""}
                  onChange={(e) =>
                    handleInputChange(e.target.value, "whatsapp_meta_token")
                  }
                  error={!!errors.whatsapp_meta_token}
                  helperText={errors.whatsapp_meta_token}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle token visibility"
                          onClick={() => setShowToken(!showToken)}
                          edge="end"
                          size="small"
                        >
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            {/* Field 2: WhatsApp Phone Number ID */}
            <Grid item xs={12} sm={5.5} sx={{ mb: 1 }}>
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    color="inherit"
                    variant="body1"
                    sx={{ fontWeight: 500 }}
                  >
                    WhatsApp Phone Number ID
                  </Typography>
                  <Tooltip
                    title="Enter the Phone Number ID from your Meta WhatsApp Cloud API configuration. This is not your actual WhatsApp mobile number."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. 1340346812488230"
                  value={config?.phone_number_id || ""}
                  onChange={(e) =>
                    handleInputChange(e.target.value, "phone_number_id")
                  }
                  error={!!errors?.phone_number_id}
                  helperText={errors?.phone_number_id}
                />
              </Box>
            </Grid>

            {/* Connection Status & Auto-fetched Metadata */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Connection Status:
                </Typography>
                {renderStatusChip()}
                {config?.verified_name && (
                  <Typography variant="body2" color="text.secondary">
                    Verified Name: <strong>{config?.verified_name}</strong>
                  </Typography>
                )}
                {config?.display_phone_number && (
                  <Typography variant="body2" color="text.secondary">
                    Display Number: <strong>{config?.display_phone_number}</strong>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ---- Section 2: Attendance Controls ---- */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Attendance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              WhatsApp attendance tracking controls.
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            {/* Field 1: Team WhatsApp Attendance */}
            <Grid
              item
              xs={12}
              sm={3.5}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                mr: 1.5,
                ml: 1.5,
                mb: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, pr: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography color="inherit" variant="body1">
                    Team WhatsApp Attendance
                  </Typography>
                  <Tooltip
                    title="Enable the attendance marking flow through WhatsApp for teams."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        cursor: "pointer",
                        mb: 0.2,
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
              <Switch
                checked={config?.team_whatsapp_attendance_applicable || false}
                onChange={(e) =>
                  handleToggleSwitch(
                    e.target.checked,
                    "team_whatsapp_attendance_applicable",
                  )
                }
                inputProps={{
                  "aria-label": "Switch for Team WhatsApp Attendance",
                }}
              />
            </Grid>

            {/* Field 2: Individual WhatsApp Attendance */}
            <Grid
              item
              xs={12}
              sm={3.5}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                border: "1px solid #E0E0E0",
                borderRadius: 1,
                mr: 1.5,
                ml: 1.5,
                mb: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, pr: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography color="inherit" variant="body1">
                    Individual WhatsApp Attendance
                  </Typography>
                  <Tooltip
                    title="Allow employees to mark their own attendance through WhatsApp."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        cursor: "pointer",
                        mb: 0.2,
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
              <Switch
                checked={
                  config?.individual_whatsapp_attendance_applicable || false
                }
                onChange={(e) =>
                  handleToggleSwitch(
                    e.target.checked,
                    "individual_whatsapp_attendance_applicable",
                  )
                }
                inputProps={{
                  "aria-label": "Switch for Individual WhatsApp Attendance",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default WhatsAppGeneralConnfig;
